import {
  getMilestoneAnchor,
  getMilestoneJunctionCenter,
  getMilestoneLabelSide,
} from '../../utils/milestoneUtils';

const SHRINK = 1;

function bodyColor({
  isComplete,
  isExcluded,
  isLocked,
  isBlocked,
  isActive,
  isAvailable,
}) {
  if (isComplete) return 'rgba(34, 197, 94, 0.1)';
  if (isExcluded) return 'rgba(239, 68, 68, 0.22)';
  if (isBlocked) return 'rgba(220, 38, 38, 0.2)';
  if (isLocked) return 'rgba(251, 191, 36, 0.14)';
  if (isActive) return 'rgba(14, 165, 233, 0.66)';
  if (isAvailable) return 'rgba(110, 130, 165, 0.72)';
  return 'rgba(100, 122, 158, 0.68)';
}

function strokeEdge(ctx, edge, scale, ox, oy) {
  const pathD = edge.pathD;
  if (typeof pathD === 'string' && pathD.trim() && typeof Path2D !== 'undefined') {
    ctx.save();
    ctx.translate(ox, oy);
    ctx.scale(scale, scale);
    ctx.lineWidth = 1 / Math.max(scale, 0.0001);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.stroke(new Path2D(pathD));
    ctx.restore();
    return;
  }

  const fromPos = edge.fromPos;
  const toPos = edge.toPos;
  if (!fromPos || !toPos) return;
  const fromDims = edge.fromDims || { width: 290, height: 150 };
  const toDims = edge.toDims || { width: 290, height: 150 };
  ctx.beginPath();
  ctx.moveTo(ox + (fromPos.x + fromDims.width / 2) * scale, oy + (fromPos.y + fromDims.height) * scale);
  ctx.lineTo(ox + (toPos.x + toDims.width / 2) * scale, oy + toPos.y * scale);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

function fillDiamond(ctx, x, y, w, h) {
  ctx.beginPath();
  ctx.moveTo(x + w / 2, y);
  ctx.lineTo(x + w, y + h / 2);
  ctx.lineTo(x + w / 2, y + h);
  ctx.lineTo(x, y + h / 2);
  ctx.closePath();
  ctx.fill();
}

function fillMilestoneFlag(ctx, x, y, size, reached) {
  const s = size / 24;
  ctx.save();
  ctx.globalAlpha = reached ? 0.75 : 1;
  ctx.fillStyle = reached ? '#86efac' : '#a78bfa';
  ctx.beginPath();
  ctx.rect(x + 5 * s, y + 3 * s, Math.max(2.5 * s, 1), 18 * s);
  ctx.rect(x + 7.5 * s, y + 3 * s, 12.5 * s, 9 * s);
  ctx.fill();
  ctx.restore();
}

/**
 * Draw every minimap tile and wire into one bitmap.
 * Coordinates are CSS pixels; the caller sets the device-pixel transform.
 */
export function paintMinimap(ctx, {
  width,
  height,
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  scale,
  bounds,
  taskStates,
  tagColorByName,
  milestoneReachedById,
  primaryTagById,
}) {
  ctx.clearRect(0, 0, width, height);
  const ox = SHRINK - bounds.minX * scale;
  const oy = SHRINK - bounds.minY * scale;

  (edges || []).forEach((edge) => {
    strokeEdge(ctx, edge, scale, ox, oy);
  });

  (tasks || []).forEach((task) => {
    const pos = nodePositions[task.id];
    if (!pos) return;
    const dims = nodeDimensions[task.id] || { width: 290, height: 150 };
    const state = taskStates[task.id] || {};

    if (task.nodeType === 'milestone') {
      const anchor = getMilestoneAnchor(task);
      const hostPos = anchor?.hostTaskId ? nodePositions?.[anchor.hostTaskId] : null;
      const hostDims = anchor?.hostTaskId ? nodeDimensions?.[anchor.hostTaskId] : null;
      const labelSide = getMilestoneLabelSide(pos, dims, hostPos, hostDims);
      const junction = getMilestoneJunctionCenter(pos, dims, labelSide);
      const reached = milestoneReachedById.get(task.id) ?? false;
      const flag = Math.max(5, Math.round(Math.max(7, Math.min(11, Math.round(6 + scale * 90))) * 0.65));
      const fx = (junction.x - bounds.minX) * scale - flag / 2;
      const fy = (junction.y - bounds.minY) * scale - flag / 2;
      fillMilestoneFlag(ctx, fx, fy, flag, reached);
      return;
    }

    const x = (pos.x - bounds.minX) * scale + SHRINK;
    const y = (pos.y - bounds.minY) * scale + SHRINK;
    const primary = primaryTagById[task.id] || null;
    const strip = primary ? (tagColorByName[primary] || null) : null;
    const minH = strip ? 3 : 2;
    const w = Math.max(dims.width * scale - SHRINK * 2, 3);
    const h = Math.max(dims.height * scale - SHRINK * 2, minH);
    const subdued = state.isComplete || state.isLocked || state.isExcluded || state.isBlocked;

    ctx.save();
    ctx.globalAlpha = subdued ? 0.84 : 1;
    ctx.fillStyle = task.nodeType === 'note'
      ? 'rgba(240, 196, 138, 0.55)'
      : bodyColor(state);

    if (task.nodeType === 'decision') fillDiamond(ctx, x, y, w, h);
    else ctx.fillRect(x, y, w, h);

    if (strip && task.nodeType !== 'note') {
      ctx.globalAlpha = subdued ? 0.38 : 1;
      ctx.fillStyle = strip;
      if (task.nodeType === 'decision') {
        ctx.fillRect(x + w * 0.35, y + 1, w * 0.3, 1);
      } else {
        ctx.fillRect(x, y, w, 1);
      }
    }

    if (!subdued && task.nodeType !== 'note' && task.nodeType !== 'decision') {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 0.5, y + 0.5, Math.max(w - 1, 0), Math.max(h - 1, 0));
    }
    ctx.restore();
  });
}
